import { useEffect, useState } from 'react';
import {
  Box, Select, MenuItem, FormControl, Button, TextField, Snackbar,
  IconButton, Tooltip, Typography, InputAdornment, Link,
} from '@mui/material';
import ReactCountryFlag from 'react-country-flag';
import { makeStyles } from 'tss-react/mui';
import CloseIcon from '@mui/icons-material/Close';
import VpnLockIcon from '@mui/icons-material/VpnLock';
import QrCode2Icon from '@mui/icons-material/QrCode2';
import VisibilityIcon from '@mui/icons-material/Visibility';
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff';
import { useTheme } from '@mui/material/styles';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { sessionActions } from '../store';
import { useLocalization, useTranslation } from '../common/components/LocalizationProvider';
import LoginLayout from './LoginLayout';
import usePersistedState from '../common/util/usePersistedState';
import {
  generateLoginToken, handleLoginTokenListeners, nativeEnvironment, nativePostMessage,
} from '../common/components/NativeInterface';
import { useCatch } from '../reactHelper';
import QrCodeDialog from '../common/components/QrCodeDialog';
import fetchOrThrow from '../common/util/fetchOrThrow';
import PlayStoreImage from '../resources/images/custom/playstore.svg';
import AppStoreImage from '../resources/images/custom/applestore.svg';
import { LoadingButton } from './components';
import getLoginTokens from './loginTokens';

const useStyles = makeStyles()((theme) => {
  const isDark = theme.palette.mode === 'dark';
  const P = getLoginTokens(isDark, theme.palette.primary.main);
  return {
    formCard: {
      animation: 'loginSlideUp 0.6s cubic-bezier(0.16, 1, 0.3, 1) forwards',
      opacity: 0,
    },
    customInput: {
      '& .MuiOutlinedInput-root': {
        borderRadius: '10px !important',
        backgroundColor: `${P.inputBg} !important`,
        fontSize: '0.92rem',
      },
      '& .MuiOutlinedInput-root fieldset': {
        borderColor: `${P.inputBorder} !important`,
        borderWidth: '1.5px !important',
      },
      '& .MuiOutlinedInput-root:hover fieldset': {
        borderColor: `${P.inputBorderHover} !important`,
      },
      '& .MuiOutlinedInput-root.Mui-focused fieldset': {
        borderColor: `${P.primary} !important`,
        borderWidth: '1.5px !important',
        boxShadow: `0 0 0 3px ${P.focusShadow}`,
      },
      '& .MuiOutlinedInput-input': {
        padding: '0.85rem 1rem',
      },
    },
    appLinksContainer: {
      display: 'flex',
      justifyContent: 'center',
      gap: theme.spacing(2),
      marginTop: theme.spacing(2),
      paddingTop: theme.spacing(2),
      borderTop: `1px solid ${P.divider}`,
      width: '100%',
    },
    appLink: {
      height: '44px',
      transition: 'transform 0.2s ease-in-out',
      '&:hover': { transform: 'scale(1.05)' },
    },
  };
});

const LoginPage = () => {
  const revenda = useSelector((state) => state.session.revenda);
  const serverAttributes = useSelector((state) => state.session.server.attributes);
  const { classes } = useStyles();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const theme = useTheme();
  const t = useTranslation();
  const isDark = theme.palette.mode === 'dark';
  const P = getLoginTokens(isDark, theme.palette.primary.main);

  const { languages, language, setLocalLanguage } = useLocalization();
  const languageList = Object.entries(languages).map(([code, { country, name }]) => ({
    code, country, name,
  }));

  const [failed, setFailed] = useState(false);
  const [email, setEmail] = usePersistedState('loginEmail', '');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [code, setCode] = useState('');
  const [showServerTooltip, setShowServerTooltip] = useState(false);
  const [showQr, setShowQr] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [loginSuccess, setLoginSuccess] = useState(false);

  const registrationEnabled = useSelector((state) => state.session.server.registration);
  const languageEnabled = useSelector((state) => {
    const attributes = state.session.server.attributes;
    return !attributes.language && !attributes['ui.disableLoginLanguage'];
  });
  const changeEnabled = useSelector((state) => !state.session.server.attributes.disableChange);
  const emailEnabled = useSelector((state) => state.session.server.emailEnabled);
  const openIdEnabled = useSelector((state) => state.session.server.openIdEnabled);
  const openIdForced = useSelector((state) => state.session.server.openIdEnabled && state.session.server.openIdForce);
  const [codeEnabled, setCodeEnabled] = useState(false);

  const [announcementShown, setAnnouncementShown] = useState(false);
  const announcement = useSelector((state) => state.session.server.announcement);

  const handlePasswordLogin = async (event) => {
    event.preventDefault();
    setFailed(false);
    setIsSubmitting(true);
    try {
      const query = `email=${encodeURIComponent(email)}&password=${encodeURIComponent(password)}`;
      const response = await fetch('/api/session', {
        method: 'POST',
        body: new URLSearchParams(code.length ? `${query}&code=${code}` : query),
      });
      if (response.ok) {
        const user = await response.json();
        generateLoginToken();
        dispatch(sessionActions.updateUser(user));
        setLoginSuccess(true);
        setTimeout(() => {
          const postLogin = window.sessionStorage.getItem('postLogin');
          window.sessionStorage.removeItem('postLogin');
          const defaultRoute = serverAttributes?.startDashboardDefault ? '/overview' : '/';
          const target = postLogin || defaultRoute;
          navigate(target, { replace: true });
        }, 800);
      } else if (response.status === 401 && response.headers.get('WWW-Authenticate') === 'TOTP') {
        setCodeEnabled(true);
        setIsSubmitting(false);
      } else {
        throw Error(await response.text());
      }
    } catch {
      setFailed(true);
      setPassword('');
      setIsSubmitting(false);
    }
  };

  const handleTokenLogin = useCatch(async (token) => {
    const response = await fetchOrThrow(`/api/session?token=${encodeURIComponent(token)}`);
    const user = await response.json();
    dispatch(sessionActions.updateUser(user));
    const defaultRoute = serverAttributes?.startDashboardDefault ? '/overview' : '/';
    navigate(defaultRoute);
  });

  const handleOpenIdLogin = () => {
    document.location = '/api/session/openid/auth';
  };

  useEffect(() => nativePostMessage('authentication'), []);

  useEffect(() => {
    const listener = (token) => handleTokenLogin(token);
    handleLoginTokenListeners.add(listener);
    return () => handleLoginTokenListeners.delete(listener);
  }, []);

  useEffect(() => {
    if (window.localStorage.getItem('hostname') !== window.location.hostname) {
      window.localStorage.setItem('hostname', window.location.hostname);
      setShowServerTooltip(true);
    }
  }, []);

  const labelSx = {
    display: 'block',
    fontSize: '0.82rem',
    fontWeight: 600,
    color: P.textPrimary,
    mb: 0.5,
  };

  return (
    <LoginLayout>
      <style>{`@keyframes loginSlideUp { from { opacity:0; transform:translateY(24px); } to { opacity:1; transform:translateY(0); } }`}</style>
      {/* ── Form card ── */}
      <Box className={classes.formCard} sx={{ width: '100%', maxWidth: 400, mx: 'auto', pt: { xs: 1, md: 0 } }}>

        {/* Title */}
        <Typography
          variant="h5"
          sx={{
            fontWeight: 700,
            color: P.textPrimary,
            mb: { xs: 3, md: 4 },
            textAlign: 'center',
            letterSpacing: '-0.01em',
            fontSize: { xs: '1.35rem', md: '1.55rem' },
          }}
        >
          {t('loginWelcome')}
        </Typography>

        {!openIdForced ? (
          <Box
            component="form"
            onSubmit={handlePasswordLogin}
            noValidate
          >
            {/* ── Email ── */}
            <Box sx={{ mb: 1.5 }}>
              <Typography component="label" htmlFor="login-email" sx={labelSx}>
                {t('userEmail')}
              </Typography>
              <TextField
                id="login-email"
                fullWidth
                placeholder={t('loginEmailPlaceholder')}
                name="email"
                value={email}
                autoComplete="email"
                autoFocus={!email}
                onChange={(e) => setEmail(e.target.value)}
                error={failed}
                variant="outlined"
                size="small"
                className={classes.customInput}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke={P.icon} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <circle cx="12" cy="12" r="10" />
                        <circle cx="12" cy="10" r="3" />
                        <path d="M6.17 18.35A4 4 0 0 1 10 16h4a4 4 0 0 1 3.83 2.35" />
                      </svg>
                    </InputAdornment>
                  ),
                }}
              />
            </Box>

            {/* ── Password ── */}
            <Box sx={{ mb: 1.2 }}>
              <Typography component="label" htmlFor="login-password" sx={labelSx}>
                {t('userPassword')}
              </Typography>
              <TextField
                id="login-password"
                fullWidth
                placeholder="••••••••••"
                name="password"
                value={password}
                type={showPassword ? 'text' : 'password'}
                autoComplete="current-password"
                autoFocus={!!email}
                onChange={(e) => setPassword(e.target.value)}
                error={failed}
                variant="outlined"
                size="small"
                className={classes.customInput}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        onClick={() => setShowPassword(!showPassword)}
                        edge="end"
                        size="small"
                        sx={{ color: P.icon, '&:hover': { color: P.primary } }}
                      >
                        {showPassword ? <VisibilityOffIcon fontSize="small" /> : <VisibilityIcon fontSize="small" />}
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
              />
            </Box>

            {/* ── TOTP code (conditional) ── */}
            {codeEnabled && (
              <Box sx={{ mb: 1.2 }}>
                <Typography component="label" htmlFor="login-code" sx={labelSx}>
                  {t('loginTotpCode')}
                </Typography>
                <TextField
                  id="login-code"
                  fullWidth
                  name="code"
                  value={code}
                  onChange={(e) => setCode(e.target.value)}
                  error={failed}
                  variant="outlined"
                  size="small"
                  className={classes.customInput}
                />
              </Box>
            )}

            {/* ── Error message ── */}
            {failed && (
              <Typography sx={{ color: theme.palette.error.main, fontSize: '0.82rem', mb: 1 }}>
                {t('loginFailed')}
              </Typography>
            )}

            {/* ── Forgot row ── */}
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', mb: 2, mt: -0.2 }}>
              {emailEnabled && (
                <Link
                  component="button"
                  type="button"
                  onClick={() => navigate('/reset-password')}
                  underline="none"
                  sx={{
                    fontSize: '0.82rem',
                    fontWeight: 600,
                    color: P.primary,
                    '&:hover': { color: P.primaryHover },
                    cursor: 'pointer',
                  }}
                >
                  {t('loginReset')}
                </Link>
              )}
            </Box>

            {/* ── Login button ── */}
            <LoadingButton
              onClick={handlePasswordLogin}
              loading={isSubmitting}
              success={loginSuccess}
              error={failed}
              disabled={!email || !password || (codeEnabled && !code)}
            >
              {t('loginLogin')}
            </LoadingButton>

            {/* ── OpenID (optional, non-forced) ── */}
            {openIdEnabled && (
              <Button
                onClick={handleOpenIdLogin}
                variant="outlined"
                fullWidth
                sx={{
                  mt: 1.5,
                  borderRadius: '10px',
                  textTransform: 'none',
                  fontWeight: 600,
                  borderColor: P.inputBorder,
                  color: P.textPrimary,
                  '&:hover': { borderColor: P.primary, color: P.primary },
                }}
              >
                {t('loginOpenId')}
              </Button>
            )}

            {/* ── Register link ── */}
            {registrationEnabled && (
              <Typography
                sx={{
                  textAlign: 'center',
                  mt: 2.5,
                  fontSize: '0.85rem',
                  color: P.textSecondary,
                }}
              >
                {`${t('loginNoAccount')} `}
                <Link
                  component="button"
                  type="button"
                  onClick={() => navigate('/register')}
                  underline="none"
                  sx={{
                    color: P.primary,
                    fontWeight: 700,
                    fontSize: '0.85rem',
                    cursor: 'pointer',
                    '&:hover': { color: P.primaryHover },
                  }}
                >
                  {t('loginSignUp')}
                </Link>
              </Typography>
            )}

            {/* ── App store links ── */}
            {(revenda?.googleplay || revenda?.applestore || serverAttributes?.androidAppLink || serverAttributes?.iosAppLink) && (
              <Box className={classes.appLinksContainer}>
                {(revenda?.googleplay || serverAttributes?.androidAppLink) && (
                  <a
                    href={revenda?.googleplay || serverAttributes?.androidAppLink}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    <img src={PlayStoreImage} alt="Google Play" className={classes.appLink} />
                  </a>
                )}
                {(revenda?.applestore || serverAttributes?.iosAppLink) && (
                  <a
                    href={revenda?.applestore || serverAttributes?.iosAppLink}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    <img src={AppStoreImage} alt="App Store" className={classes.appLink} />
                  </a>
                )}
              </Box>
            )}
          </Box>
        ) : (
          /* ── OpenID forced mode ── */
          <Button
            onClick={handleOpenIdLogin}
            variant="contained"
            fullWidth
            sx={{
              backgroundColor: P.primary,
              color: '#fff',
              fontWeight: 700,
              py: 1.5,
              borderRadius: '10px',
              fontSize: '0.95rem',
              textTransform: 'none',
              '&:hover': {
                backgroundColor: P.primaryHover,
                transform: 'translateY(-1px)',
                boxShadow: `0 6px 20px ${P.btnShadow}`,
              },
            }}
          >
            {t('loginOpenId')}
          </Button>
        )}
      </Box>

      {/* ── Options toolbar: fixed top-right (like Traccar official) ── */}
      <Box
        sx={{
          position: 'fixed',
          top: { xs: 12, md: 16 },
          right: { xs: 12, md: 20 },
          display: 'flex',
          flexDirection: 'row',
          alignItems: 'center',
          gap: 0.5,
          zIndex: 20,
        }}
      >
        {(nativeEnvironment || window.location.hostname !== 'localhost') && changeEnabled && (
          <IconButton
            onClick={() => navigate('/change-server')}
            sx={{
              color: P.textSecondary,
              '&:hover': { color: P.primary, backgroundColor: P.iconHoverBg },
            }}
          >
            <Tooltip
              title={`${t('settingsServer')}: ${window.location.hostname}`}
              open={showServerTooltip}
              arrow
            >
              <VpnLockIcon />
            </Tooltip>
          </IconButton>
        )}
        {!nativeEnvironment && (
          <IconButton
            onClick={() => setShowQr(true)}
            sx={{
              color: P.textSecondary,
              '&:hover': { color: P.primary, backgroundColor: P.iconHoverBg },
            }}
          >
            <QrCode2Icon />
          </IconButton>
        )}
        {languageEnabled && (
          <FormControl size="small" sx={{ minWidth: 0, width: 'auto' }}>
            <Select
              value={language}
              onChange={(e) => setLocalLanguage(e.target.value)}
              fullWidth={false}
              sx={{
                minHeight: 'auto',
                backgroundColor: 'transparent',
                borderRadius: '8px',
                fontSize: '0.82rem',
                color: P.textSecondary,
                '& .MuiSelect-select': {
                  py: 0.75,
                  px: 1.2,
                  display: 'flex',
                  alignItems: 'center',
                  gap: 0.5,
                },
                '& .MuiOutlinedInput-notchedOutline': {
                  borderColor: P.inputBorder,
                },
                '&:hover .MuiOutlinedInput-notchedOutline': {
                  borderColor: P.primary,
                },
                '& .MuiSelect-icon': {
                  color: P.textSecondary,
                },
              }}
            >
              {languageList.map((it) => (
                <MenuItem key={it.code} value={it.code}>
                  <Box component="span" sx={{ mr: 1 }}>
                    <ReactCountryFlag countryCode={it.country} svg />
                  </Box>
                  {it.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        )}
      </Box>

      <QrCodeDialog open={showQr} onClose={() => setShowQr(false)} />

      <Snackbar
        open={!!announcement && !announcementShown}
        message={announcement}
        action={(
          <IconButton size="small" color="inherit" onClick={() => setAnnouncementShown(true)}>
            <CloseIcon fontSize="small" />
          </IconButton>
        )}
      />
    </LoginLayout>
  );
};

export default LoginPage;
